import React, { useEffect, useState } from "react";
import {
    Grid,
    TextField,
    LinearProgress,
    Pagination,
    Paper,
    Skeleton,
    Typography,
    Box,
    Button,
    Card,
    CardMedia,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import { useNewsController } from "./context/news-controller";
import { useNewsState } from "./context/news-context";
import { IListNewsRequest } from "../../model/base/request/list-news-request";
import PageHeader from "../components/shared/page-header";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

// Componente interno para gerenciar o estado da imagem de cada card
const NewsCardItem = ({ item, onClick }: { item: any; onClick: () => void }) => {
    const [imageError, setImageError] = useState(false);

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                },
            }}
            onClick={onClick}
        >
            {item.urlImageEmphasis && !imageError ? (
                <CardMedia
                    component="img"
                    height="200"
                    image={item.urlImageEmphasis}
                    alt={item.title}
                    onError={() => setImageError(true)}
                    sx={{
                        objectFit: 'cover',
                    }}
                />
            ) : (
                <Box
                    sx={{
                        height: 200,
                        backgroundColor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Sem imagem
                    </Typography>
                </Box>
            )}

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {item.category && (
                    <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            mb: 1,
                        }}
                    >
                        {item.category}
                    </Typography>
                )}

                <Typography
                    gutterBottom
                    variant="h6"
                    component="h3"
                    sx={{
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        lineHeight: 1.4,
                        color: 'text.primary',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mb: 'auto',
                    }}
                >
                    {item.title}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {item.publishedDate ? dayjs(item.publishedDate).format('DD/MM/YYYY') : ''}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                            fontWeight: 600,
                        }}
                    >
                        Leia mais →
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export function NewsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData, loadCategories } = useNewsController();
    const { news, categories } = useNewsState();
    const [paramsRequet, setParamsRequest] = useState<IListNewsRequest>({ page: 0, pageSize: 12 } as IListNewsRequest);
    const navigate = useNavigate();

    useEffect(() => {
        const initData = async () => {
            await loadCategories();
        };
        initData();
    }, []);

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequet);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequet]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 12 } as IListNewsRequest);
    };

    const handleNewsClick = (newsId: number | undefined) => {
        if (newsId) {
            navigate(`/noticia/${newsId}`);
        }
    };

    return (
        <Box>
            <PageHeader
                title="Notícias"
                subtitle="Acompanhe as últimas notícias e informações do município"
                breadcrumbs={[{ label: 'Notícias' }]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={5}>
                                <TextField
                                    fullWidth
                                    label="Buscar notícias"
                                    placeholder="Digite para buscar notícias"
                                    value={paramsRequet.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequet, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={paramsRequet.categoryId || ''}
                                        label="Categoria"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequet,
                                                page: 0,
                                                categoryId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {categories.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        {loadMoreData && <LinearProgress sx={{ mb: 2 }} />}

                        {news?.items && news.items.length > 0 ? (
                            <Grid container spacing={3}>
                                {news.items.map((item) => (
                                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                                        <NewsCardItem
                                            item={item}
                                            onClick={() => handleNewsClick(item.id)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Nenhuma notícia encontrada
                                </Typography>
                            </Box>
                        )}

                        {news?.pageCount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={news.pageCount}
                                    page={(paramsRequet.page || 0) + 1}
                                    onChange={(_, page) => {
                                        setParamsRequest({ ...paramsRequet, page: page - 1 });
                                    }}
                                    disabled={loadMoreData}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                />
                            </Box>
                        )}
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
